import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import './About.css';

const About = () => {
  const values = [
    {
      icon: 'bi-heart-fill',
      title: 'Compassion',
      description: 'We approach every interaction with empathy and understanding.',
      color: 'bg-primary-orange',
    },
    {
      icon: 'bi-shield-check',
      title: 'Integrity',
      description: 'We maintain the highest standards of honesty and transparency.',
      color: 'bg-primary-teal',
    },
    {
      icon: 'bi-people-fill',
      title: 'Community',
      description: 'We believe in the strength and resilience of community bonds.',
      color: 'bg-primary-purple',
    },
    {
      icon: 'bi-lightbulb-fill',
      title: 'Innovation',
      description: 'We continuously evolve to meet the changing needs of our community.',
      color: 'bg-primary-teal',
    },
  ];

  const team = [
    {
      name: 'Community Advocates',
      role: 'Connecting You with Resources',
      description: 'Our advocates work tirelessly to understand your unique needs and connect you with the right resources.',
    },
    {
      name: 'Career Counselors',
      role: 'Guiding Your Career Path',
      description: 'Experienced professionals who help you discover opportunities and achieve your career goals.',
    },
    {
      name: 'Tax Specialists',
      role: 'Maximizing Your Returns',
      description: 'Certified experts who ensure you receive every benefit and credit you deserve.',
    },
    {
      name: 'Consultants',
      role: 'Providing Expert Guidance',
      description: 'Strategic advisors who help you navigate complex decisions with confidence.',
    },
  ];

  const problems = [
    {
      icon: 'bi-x-circle-fill',
      problem: 'Limited Access to Resources',
      solution: 'We bridge the gap by connecting you with comprehensive community resources.',
    },
    {
      icon: 'bi-x-circle-fill',
      problem: 'Unemployment & Underemployment',
      solution: 'Our job placement services match your skills with meaningful opportunities.',
    },
    {
      icon: 'bi-x-circle-fill',
      problem: 'Complex Tax Preparation',
      solution: 'Expert tax services that maximize your returns and minimize stress.',
    },
    {
      icon: 'bi-x-circle-fill',
      problem: 'Lack of Guidance & Support',
      solution: 'Personalized consulting to help you make informed decisions.',
    },
  ];

  return (
    <div className="about-page mt-4">
      {/* Hero Section */}
      <section className="c-about-hero position-relative overflow-hidden py-5 py-lg-6" style={{background: 'linear-gradient(135deg, rgba(236, 232, 247, 0.5) 0%, rgba(255, 255, 255, 1) 100%)'}}>
        <div className="position-absolute" style={{top: '0', left: '10%', width: '500px', height: '500px', backgroundImage: 'radial-gradient(circle, rgba(251, 146, 60, 0.06) 0%, transparent 70%)', borderRadius: '50%'}}></div>
        <div className="container position-relative">
          <div className="row align-items-center g-5">
            <AnimatedSection className="col-lg-6">
              <div className="c-hero-badge bg-primary-purple bg-opacity-10 text-primary-purple d-inline-flex align-items-center px-4 py-2 rounded-pill mb-4 shadow-sm">
                <i className="bi bi-people-fill me-2 fs-5"></i>
                <span className="fw-bold">Our Story</span>
              </div>
              <h1 className="display-3 fw-bold mb-4 text-dark" style={{letterSpacing: '-1px'}}>
                Who We Are
              </h1>
              <p className="fs-5 text-muted lh-lg mb-4">
                MWC Connections is more than a service provider—we are your partners in building
                a brighter future. Since 2009, we have been dedicated to bridging communities and
                empowering individuals through comprehensive support services.
              </p>
              <p className="fs-6 text-muted lh-lg mb-4">
                We understand that navigating life's challenges can be overwhelming. That's why we're
                here to provide the guidance, resources, and connections you need to succeed.
              </p>
              <div className="c-hero-features d-flex flex-wrap gap-3">
                <div className="c-feature-badge bg-white rounded-pill px-4 py-2 shadow-sm d-inline-flex align-items-center">
                  <i className="bi bi-award-fill text-primary-teal me-2"></i>
                  <span className="fw-semibold small">Trusted Partner</span>
                </div>
                <div className="c-feature-badge bg-white rounded-pill px-4 py-2 shadow-sm d-inline-flex align-items-center">
                  <i className="bi bi-shield-check text-primary-orange me-2"></i>
                  <span className="fw-semibold small">Proven Results</span>
                </div>
                <div className="c-feature-badge bg-white rounded-pill px-4 py-2 shadow-sm d-inline-flex align-items-center">
                  <i className="bi bi-heart-fill text-primary-purple me-2"></i>
                  <span className="fw-semibold small">Community First</span>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection className="col-lg-6" delay={0.2}>
              <div className="c-about-stats-grid row g-3">
                <div className="col-6">
                  <motion.div
                    className="c-about-stat-card bg-white rounded-4 shadow-sm p-4 text-center border border-1 border-light h-100"
                    whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="c-stat-icon bg-primary-teal bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                      <i className="bi bi-calendar-check-fill text-primary-teal fs-4"></i>
                    </div>
                    <h2 className="display-5 fw-bold text-primary-teal mb-2">15+</h2>
                    <p className="text-muted fw-medium mb-0">Years of Service</p>
                  </motion.div>
                </div>
                <div className="col-6">
                  <motion.div
                    className="c-about-stat-card bg-white rounded-4 shadow-sm p-4 text-center border border-1 border-light h-100"
                    whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="c-stat-icon bg-primary-orange bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                      <i className="bi bi-people-fill text-primary-orange fs-4"></i>
                    </div>
                    <h2 className="display-5 fw-bold text-primary-orange mb-2">5000+</h2>
                    <p className="text-muted fw-medium mb-0">Lives Impacted</p>
                  </motion.div>
                </div>
                <div className="col-12">
                  <motion.div
                    className="c-about-stat-card bg-white rounded-4 shadow-sm p-4 text-center border border-1 border-light"
                    whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="c-stat-icon bg-primary-purple bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                      <i className="bi bi-star-fill text-primary-purple fs-4"></i>
                    </div>
                    <h2 className="display-5 fw-bold text-primary-purple mb-2">98%</h2>
                    <p className="text-muted fw-medium mb-0">Client Satisfaction Rate</p>
                  </motion.div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="problems-section section-padding">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Problems We Solve</h2>
            <p>Understanding challenges and providing real solutions</p>
          </AnimatedSection>

          <div className="row g-4">
            {problems.map((item, index) => (
              <AnimatedSection className="col-lg-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="problem-card"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="problem-header">
                    <i className={`${item.icon} text-danger`}></i>
                    <h5>{item.problem}</h5>
                  </div>
                  <div className="solution">
                    <i className="bi bi-check-circle-fill text-success"></i>
                    <p>{item.solution}</p>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Community Bridging Matters */}
      <section className="importance-section section-padding bg-gradient-section">
        <div className="container">
          <div className="row align-items-center">
            <AnimatedSection className="col-lg-6">
              <h2 className="text-white mb-4">Why Community Bridging Matters</h2>
              <div className="importance-content">
                <div className="importance-item">
                  <i className="bi bi-arrow-right-circle-fill"></i>
                  <div>
                    <h5>Breaking Down Barriers</h5>
                    <p>Many people face systemic barriers to accessing resources and opportunities.
                    We work to eliminate these obstacles.</p>
                  </div>
                </div>
                <div className="importance-item">
                  <i className="bi bi-arrow-right-circle-fill"></i>
                  <div>
                    <h5>Strengthening Communities</h5>
                    <p>When individuals thrive, entire communities benefit. We create ripple effects
                    of positive change.</p>
                  </div>
                </div>
                <div className="importance-item">
                  <i className="bi bi-arrow-right-circle-fill"></i>
                  <div>
                    <h5>Empowering Individuals</h5>
                    <p>We don't just provide services—we equip people with tools and knowledge to
                    achieve long-term success.</p>
                  </div>
                </div>
                <div className="importance-item">
                  <i className="bi bi-arrow-right-circle-fill"></i>
                  <div>
                    <h5>Building Equity</h5>
                    <p>Everyone deserves equal access to opportunities. We work to level the playing
                    field for all community members.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection className="col-lg-6" delay={0.2}>
              <div className="importance-visual">
                <div className="visual-card">
                  <i className="bi bi-diagram-3-fill"></i>
                  <h4>Connected Community</h4>
                  <p>Building networks that last</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section-padding">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Our Core Values</h2>
            <p>Principles that guide everything we do</p>
          </AnimatedSection>

          <div className="row g-4">
            {values.map((value, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="value-card text-center"
                  whileHover={{ y: -10 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className={`value-icon ${value.color}`}>
                    <i className={value.icon}></i>
                  </div>
                  <h5>{value.title}</h5>
                  <p>{value.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section section-padding bg-primary-light">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Our Team</h2>
            <p>Dedicated professionals committed to your success</p>
          </AnimatedSection>

          <div className="row g-4">
            {team.map((member, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <div className="team-card">
                  <div className="team-icon">
                    <i className="bi bi-person-circle"></i>
                  </div>
                  <h5>{member.name}</h5>
                  <p className="team-role text-primary-teal">{member.role}</p>
                  <p className="team-description">{member.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <div className="container">
          <AnimatedSection className="text-center">
            <h2 className="mb-4">Join Our Community</h2>
            <p className="mb-4 lead">
              Experience the difference that dedicated support and genuine care can make.
            </p>
            <Link to="/contact" className="btn btn-primary-teal btn-lg">
              Get Started Today
              <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default About;
